/**
 * Execution Hooks
 *
 * React Query hooks for agent execution management.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { executionService } from '../services/execution';
import {
  ExecuteAgentRequest,
  ExecutionListParams,
  ExecutionStatus,
} from '../types/execution';
import { agentKeys } from './useAgents';

// Query keys for executions
export const executionKeys = {
  all: ['executions'] as const,
  lists: () => [...executionKeys.all, 'list'] as const,
  list: (params: ExecutionListParams | Record<string, never>) => [...executionKeys.lists(), params] as const,
  details: () => [...executionKeys.all, 'detail'] as const,
  detail: (id: string) => [...executionKeys.details(), id] as const,
  byAgent: (agentId: string) => [...executionKeys.all, 'agent', agentId] as const,
  agentList: (agentId: string, params: Record<string, unknown>) =>
    [...executionKeys.byAgent(agentId), params] as const,
  stats: (agentId?: string) => [...executionKeys.all, 'stats', agentId] as const,
};

/**
 * Hook to execute an agent
 *
 * @returns Mutation object for executing an agent
 *
 * @example
 * ```tsx
 * const executeAgent = useExecuteAgent();
 *
 * const handleRun = () => {
 *   executeAgent.mutate({
 *     agentId: 'agent-123',
 *     data: { input: { prompt: 'Hello' } }
 *   });
 * };
 * ```
 */
export function useExecuteAgent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ agentId, data }: { agentId: string; data?: ExecuteAgentRequest }) =>
      executionService.execute(agentId, data),
    onSuccess: (_response, variables) => {
      // Invalidate execution lists
      queryClient.invalidateQueries({ queryKey: executionKeys.lists() });
      queryClient.invalidateQueries({ queryKey: executionKeys.byAgent(variables.agentId) });
      queryClient.invalidateQueries({ queryKey: executionKeys.stats() });
      queryClient.invalidateQueries({ queryKey: executionKeys.stats(variables.agentId) });

      // Invalidate agent stats (execution count may have changed)
      queryClient.invalidateQueries({ queryKey: agentKeys.stats() });

      toast.success('Agent execution started!', {
        icon: '🚀',
        duration: 4000,
      });
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      const message = error.response?.data?.error?.message || 'Failed to execute agent';
      toast.error(message);
    },
  });
}

/**
 * Hook to fetch a single execution
 *
 * @param executionId - The execution ID to fetch
 * @param options - Additional query options
 * @returns Query result with execution data
 */
export function useExecution(
  executionId: string | undefined,
  options?: {
    /** Polling interval in ms (for tracking running executions) */
    refetchInterval?: number | false;
    /** Whether to enable polling only when execution is running */
    refetchWhileRunning?: boolean;
  }
) {
  return useQuery({
    queryKey: executionKeys.detail(executionId!),
    queryFn: () => executionService.get(executionId!),
    enabled: !!executionId,
    refetchInterval: (query) => {
      // If refetchWhileRunning is true, only poll while status is PENDING or RUNNING
      if (options?.refetchWhileRunning) {
        const status = query.state.data?.status;
        if (status === 'PENDING' || status === 'RUNNING') {
          return options.refetchInterval || 2000;
        }
        return false;
      }
      return options?.refetchInterval || false;
    },
  });
}

/**
 * Hook to list executions with pagination and filters
 *
 * @param params - Query parameters for filtering
 * @returns Query result with paginated executions
 */
export function useExecutions(params?: ExecutionListParams) {
  return useQuery({
    queryKey: executionKeys.list(params || {}),
    queryFn: () => executionService.list(params),
  });
}

/**
 * Hook to list executions for a specific agent
 *
 * @param agentId - The agent ID to filter by
 * @param params - Additional query parameters
 * @returns Query result with paginated executions for the agent
 */
export function useAgentExecutions(
  agentId: string | undefined,
  params?: Omit<ExecutionListParams, 'agentId'>
) {
  return useQuery({
    queryKey: executionKeys.agentList(agentId!, params || {}),
    queryFn: () => executionService.listByAgent(agentId!, params),
    enabled: !!agentId,
  });
}

/**
 * Hook to cancel a running execution
 *
 * @returns Mutation object for cancelling an execution
 */
export function useCancelExecution() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (executionId: string) => executionService.cancel(executionId),
    onSuccess: (execution) => {
      // Update the execution in cache
      queryClient.setQueryData(executionKeys.detail(execution.id), execution);
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: executionKeys.lists() });
      queryClient.invalidateQueries({ queryKey: executionKeys.byAgent(execution.agentId) });
      queryClient.invalidateQueries({ queryKey: executionKeys.stats() });

      toast.success('Execution cancelled', {
        icon: '⏹️',
      });
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      const message = error.response?.data?.error?.message || 'Failed to cancel execution';
      toast.error(message);
    },
  });
}

/**
 * Hook to replay an execution — re-run the same agent with the same input.
 * Returns the new execution so the caller can navigate to it.
 */
export function useReplayExecution() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (executionId: string) => executionService.replay(executionId),
    onSuccess: (execution) => {
      queryClient.invalidateQueries({ queryKey: executionKeys.lists() });
      queryClient.invalidateQueries({ queryKey: executionKeys.byAgent(execution.agentId) });
      queryClient.invalidateQueries({ queryKey: executionKeys.stats() });
      toast.success('Replaying execution', { icon: '🔁' });
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      const message = error.response?.data?.error?.message || 'Failed to replay execution';
      toast.error(message);
    },
  });
}

/**
 * Hook to fetch execution statistics
 *
 * @param agentId - Optional agent ID to filter stats
 * @returns Query result with execution statistics
 */
export function useExecutionStats(agentId?: string) {
  return useQuery({
    queryKey: executionKeys.stats(agentId),
    queryFn: () => executionService.getStats(agentId),
  });
}

/**
 * Helper to get status badge color
 */
export function getExecutionStatusColor(status: ExecutionStatus): string {
  switch (status) {
    case 'PENDING':
      return 'bg-yellow-100 text-yellow-800';
    case 'RUNNING':
      return 'bg-blue-100 text-blue-800';
    case 'COMPLETED':
      return 'bg-green-100 text-green-800';
    case 'FAILED':
      return 'bg-red-100 text-red-800';
    case 'CANCELLED':
      return 'bg-gray-100 text-gray-800';
    case 'TIMEOUT':
      return 'bg-orange-100 text-orange-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
}

/**
 * Helper to get status icon
 */
export function getExecutionStatusIcon(status: ExecutionStatus): string {
  switch (status) {
    case 'PENDING':
      return '⏳';
    case 'RUNNING':
      return '🔄';
    case 'COMPLETED':
      return '✅';
    case 'FAILED':
      return '❌';
    case 'CANCELLED':
      return '⏹️';
    case 'TIMEOUT':
      return '⏰';
    default:
      return '❓';
  }
}

/**
 * Helper to format duration
 */
export function formatDuration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return '-';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  const minutes = Math.floor(ms / 60000);
  const seconds = Math.floor((ms % 60000) / 1000);
  return `${minutes}m ${seconds}s`;
}

/**
 * Format an estimated run cost, given in CENTS.
 *
 * Precision adapts to size because a typical agent run costs well under a cent
 * (~0.1c). Fixed 2-decimal dollars would render every real run as "$0.00" —
 * which is the exact bug this data was fixed to stop reporting.
 */
export function formatCents(cents: number | null | undefined): string {
  // Number.isFinite also rejects NaN/Infinity, which Intl happily renders as
  // "$NaN"/"$∞". Unreachable from the API today, but this is a shared utility and
  // the next caller doing arithmetic on two floats has no compile-time guard.
  if (cents == null || !Number.isFinite(cents)) return '-';
  if (cents === 0) return '$0.00';

  const dollars = cents / 100;
  // Enough decimals to show a real value, capped so it stays readable.
  const decimals = dollars >= 1 ? 2 : dollars >= 0.01 ? 4 : 6;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: decimals,
  }).format(dollars);
}

/**
 * Format a token count (e.g. 5180 -> "5,180").
 */
export function formatTokens(tokens: number | null | undefined): string {
  if (tokens == null || !Number.isFinite(tokens)) return '-';
  return new Intl.NumberFormat('en-US').format(tokens);
}
